import { ErrorBoundary, RollbarContext } from '@rollbar/react'
import * as actions from 'client/actions/actionsManagement'
import { getPreviewPageData } from 'client/actions/pageActions'
import { WORK_AREA_ID } from 'client/constants/editorIds'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement, { selectors } from 'client/hooks/useManagement'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { useWebsiteSettings } from 'client/store'
import { useActions } from 'common/hooks/useActions'
import React, {
  KeyboardEvent,
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Trans } from 'react-i18next'
import { fetchWebsitePreview } from 'src/editor/api/website-websites-api'
import {
  getWebsiteId,
  getWebsiteLayoutPageId,
} from 'src/editor/store/website-settings/website-settings-selector'
import { twMerge } from 'tailwind-merge'
import { PreviewNavigation } from '../preview-navigation/preview-navigation'
import { WebsiteContentSkeleton } from './website-content-skeleton'

export const WebsiteContent = memo(() => {
  const contentRef = useRef<HTMLElement | null>(null)
  const { isLandscape, isMobile } = useDeviceModeContext()
  const isSomethingEditing = useManagement(selectors.isSomethingEditing)
  const toggleSettings = useActions(actions.toggleSettings)
  const websiteId = useWebsiteSettings(getWebsiteId)
  const websiteLayoutId = useWebsiteSettings(getWebsiteLayoutPageId)
  const { pageId: websitePageId } = usePageRouteDetector()
  const [previewHtml, setPreviewHtml] = useState<string>()
  const getPreviewData = useActions(getPreviewPageData)
  const [isLoading, setIsLoading] = useState(false)

  const disableMobileSettings = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      if ('key' in e) {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return
        }
      }

      if (isMobile && isSomethingEditing && contentRef.current === e.target) {
        toggleSettings(null)
      }
    },
    [isMobile, isSomethingEditing, toggleSettings],
  )

  useEffect(() => {
    if (!websiteId || !websiteLayoutId || !websitePageId) {
      return
    }

    const fetchHtml = async () => {
      setIsLoading(true)
      const pageData = getPreviewData()

      const { previewHtml } = await fetchWebsitePreview({
        websiteId,
        websiteLayoutId,
        websitePageId: Number.parseInt(websitePageId),
        pagePreviewData: pageData,
      })

      setPreviewHtml(previewHtml)
      setIsLoading(false)
    }

    fetchHtml()
  }, [getPreviewData, websiteId, websiteLayoutId, websitePageId])

  if (isLoading) {
    return (
      <>
        <PreviewNavigation />
        <WebsiteContentSkeleton />
      </>
    )
  }

  return (
    <>
      <PreviewNavigation />
      <RollbarContext context="editor">
        <ErrorBoundary
          fallbackUI={() => (
            <Trans parent="h3">core.errors.went_wrong_contact_support</Trans>
          )}
        >
          <main
            className={twMerge(
              'relative flex h-dvh justify-center',
              isMobile && 'mt-12 items-center',
            )}
            onClick={disableMobileSettings}
            onKeyUp={disableMobileSettings}
            ref={contentRef}
          >
            {isMobile && (
              <img
                className={twMerge(
                  'pointer-events-none absolute z-10 h-200 w-110',
                  isLandscape && 'rotate-90',
                )}
                src={`${process.env.CLOUDFRONT_SHARED}/images/preview_frame_phone.png`}
                alt="Preview Frame"
              />
            )}
            <div
              className={twMerge(
                isMobile ? 'max-h-200 max-w-96 ' : 'w-full',
                isLandscape && 'max-h-100 max-w-200',
              )}
            >
              <div
                id={WORK_AREA_ID}
                className={twMerge(
                  'h-[calc(100vh-var(--layout-header-height))]',
                  isMobile
                    ? 'h-190 w-96 scroll-smooth'
                    : 'flex h-full flex-col overflow-hidden',
                  isLandscape && 'h-100 w-200',
                )}
              >
                <div
                  className={twMerge(
                    'h-200 w-96',
                    isLandscape && 'h-100 w-200',
                  )}
                >
                  <iframe
                    id="editor-preview-iframe"
                    className={twMerge(
                      'no-scrollbar absolute h-full w-full ',
                      isMobile && 'h-190 w-96 rounded-[60px]',
                      isLandscape && 'h-100 w-195 pl-6',
                    )}
                    title="Preview"
                    srcDoc={previewHtml}
                  />
                </div>
              </div>
            </div>
          </main>
        </ErrorBoundary>
      </RollbarContext>
    </>
  )
})
